// App store links and the device check behind the download link (accruewise.in/download,
// also /app and /get). One link for every post, bio, QR code and video: Android goes to
// Google Play, iPhone to the App Store (or the waitlist until the iPhone app is live), and
// computers see the /download page with a QR code.
//
// The inline script at the top of public/index.html repeats this redirect for cold visits,
// so phones leave before the app's JavaScript loads. Keep the two in step.

export const PLAY_PACKAGE = "com.accruewise.app";
export const PLAY_STORE_BASE = `https://play.google.com/store/apps/details?id=${PLAY_PACKAGE}`;

// Set REACT_APP_APP_STORE_URL (e.g. https://apps.apple.com/in/app/accruewise/id1234567890)
// once the iPhone app is public. Until then iPhones are sent to the waitlist.
export const APP_STORE_URL = (process.env.REACT_APP_APP_STORE_URL || "").trim();
// Optional App Store Connect provider token, for App Analytics campaign attribution.
export const APP_STORE_PROVIDER_TOKEN = (process.env.REACT_APP_APP_STORE_PT || "").trim();
export const IOS_LIVE = /^https:\/\/apps\.apple\.com\//.test(APP_STORE_URL);

export const DOWNLOAD_PATHS = ["/download", "/app", "/get"];
export const DOWNLOAD_LINK = "https://accruewise.in/download";

export function detectPlatform(ua = typeof navigator !== "undefined" ? navigator.userAgent : "", touchPoints = typeof navigator !== "undefined" ? navigator.maxTouchPoints : 0) {
    if (/android/i.test(ua)) return "android";
    if (/iphone|ipad|ipod/i.test(ua)) return "ios";
    // iPadOS reports itself as a Mac; a touch screen gives it away.
    if (/macintosh/i.test(ua) && touchPoints > 1) return "ios";
    return "desktop";
}

// Attribution values are kept to a safe alphabet so a pasted link can't inject anything.
const clean = (v, fallback) => {
    const s = String(v || "").trim().slice(0, 64);
    return /^[A-Za-z0-9_.-]+$/.test(s) ? s : fallback;
};

/** UTM fields from a query string, with the download link's defaults. */
export function utmFrom(search = "") {
    const q = new URLSearchParams(search);
    return {
        source: clean(q.get("utm_source"), "download_link"),
        medium: clean(q.get("utm_medium"), "link"),
        campaign: clean(q.get("utm_campaign"), "download"),
        content: clean(q.get("utm_content"), ""),
    };
}

/** Google Play with install-referrer attribution. */
export function playUrl({ source = "download_link", medium = "link", campaign = "download", content = "" } = {}) {
    const parts = [`utm_source=${source}`, `utm_medium=${medium}`, `utm_campaign=${campaign}`];
    if (content) parts.push(`utm_content=${content}`);
    return `${PLAY_STORE_BASE}&referrer=${encodeURIComponent(parts.join("&"))}`;
}

/** The App Store listing (with a campaign token when configured), or null before launch. */
export function appStoreUrl({ campaign = "download" } = {}) {
    if (!IOS_LIVE) return null;
    const u = new URL(APP_STORE_URL);
    if (APP_STORE_PROVIDER_TOKEN) {
        u.searchParams.set("pt", APP_STORE_PROVIDER_TOKEN);
        u.searchParams.set("ct", campaign);
        u.searchParams.set("mt", "8");
    }
    return u.toString();
}

/** Where iPhones go while the App Store app isn't public: the waitlist, attribution kept. */
export function iosWaitlistUrl({ source = "download_link", medium = "link", campaign = "download" } = {}) {
    return `/waitlist?platform=ios&utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`;
}

/** The store for a platform, or null for computers (they get the /download page). */
export function storeUrlFor(platform, utm = {}) {
    if (platform === "android") return playUrl(utm);
    if (platform === "ios") return appStoreUrl(utm) || iosWaitlistUrl(utm);
    return null;
}

/**
 * For "Get the app" buttons: the right store for this device, or the /download page (QR
 * code) on a computer. `placement` becomes the campaign, like the other website CTAs.
 */
export function downloadHref(placement) {
    const utm = { source: "accruewise_website", medium: "cta", campaign: clean(placement, "website") };
    return storeUrlFor(detectPlatform(), utm) || `/download?utm_source=${utm.source}&utm_medium=${utm.medium}&utm_campaign=${utm.campaign}`;
}
